import React, { useState } from "react";
import {
  CrossIcon,
  OfficeIcon,
  SavedIcon,
  ShieldIcon,
} from "evergreen-ui";
import { COUNTRY_OPTIONS } from "../taxRules";
import { Brand, CustomSelect, IconButton, ModalLayer } from "../components/SharedUI";
import { NAVIGATION_PRESETS } from "../navigationPreferences";

const FOCUS_OPTIONS = [
  ["invoicing", "Invoices first", "Clients and reports, without task-management noise"],
  ["operations", "Invoices + work", "Add tasks, reminders, services, and history"],
  ["accounting", "Full accounting", "Include documents, banking, and QuickBooks"],
];

const IDENTITY_OPTIONS = [
  ["company", "Company", "Use a registered or trading business identity"],
  ["freelancer", "Freelancer", "Issue invoices under your professional name"],
  ["later", "Add later", "Open a blank workspace without an issuing identity"],
];

const STEP_COPY = [
  ["Your profile", "Tell Invoice Studio who is using this workspace.", "Only your name is used in local activity history. Email is optional."],
  ["Workspace focus", "Keep the first view focused.", "Choose the tools you expect to use. You can change this later from Account."],
  ["Invoice identity", "How will you issue invoices?", "Set up a company or freelancer identity now, or enter the workspace completely blank."],
];

const CITY_OPTIONS_BY_COUNTRY = {
  XK: ["Prishtina", "Prizren", "Peja", "Gjakova", "Ferizaj", "Gjilan", "Mitrovica"],
  AL: ["Tirana", "Durres", "Vlore", "Shkoder", "Elbasan", "Korce"],
  GB: ["London", "Manchester", "Birmingham", "Edinburgh", "Glasgow"],
  DE: ["Berlin", "Munich", "Hamburg", "Frankfurt", "Cologne"],
  US: ["New York", "Los Angeles", "Chicago", "San Francisco", "Austin"],
};

const hasFullName = (value) => String(value || "").trim().split(/\s+/).filter(Boolean).length >= 2;

const workspaceNameFor = (draft, identityMode) => {
  if (identityMode !== "later" && draft.businessName.trim()) return draft.businessName.trim();
  const firstName = draft.ownerName.trim().split(/\s+/)[0];
  return firstName ? `${firstName}'s workspace` : "My local workspace";
};

const LocalWorkspaceOnboarding = ({ close, complete, mode = "custom", skip }) => {
  const emptyMode = mode === "empty";
  const [step, setStep] = useState(0);
  const [identityMode, setIdentityMode] = useState("company");
  const [draft, setDraft] = useState({ businessName: "", legalName: "", businessLogo: "", ownerName: "", email: "", countryCode: "", location: "", currency: "EUR", workspaceName: "", focus: "invoicing", navigationPreferences: NAVIGATION_PRESETS.invoicing });
  const [customCity, setCustomCity] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const update = (field) => (event) => setDraft((current) => ({ ...current, [field]: event.target.value }));
  const updateCountry = (event) => {
    setCustomCity(false);
    setDraft((current) => ({ ...current, countryCode: event.target.value, location: "" }));
  };
  const chooseFocus = (focus) => setDraft((current) => ({ ...current, focus, navigationPreferences: NAVIGATION_PRESETS[focus] }));
  const chooseIdentity = (nextMode) => {
    setIdentityMode(nextMode);
    if (nextMode === "freelancer" && !draft.businessName.trim() && draft.ownerName.trim()) {
      setDraft((current) => ({ ...current, businessName: current.ownerName.trim() }));
    }
    setMessage("");
  };
  const changeBusinessLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 2 * 1024 * 1024) {
      setMessage("Choose a PNG, JPG, or SVG image smaller than 2 MB.");
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setDraft((current) => ({ ...current, businessLogo: String(reader.result) }));
      setMessage("");
    };
    reader.onerror = () => setMessage("The business image could not be read on this device.");
    reader.readAsDataURL(file);
  };

  const runComplete = async (details) => {
    setBusy(true);
    setMessage("");
    try {
      const result = await complete(details);
      if (!result?.ok) setMessage(result?.error || "This workspace could not be created in your browser.");
    } catch {
      setMessage("This workspace could not be created in your browser.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setMessage("");
    if (emptyMode && step < 2) {
      if (step === 0 && !hasFullName(draft.ownerName)) {
        setMessage("Enter your first and last name so assignments and activity are clear.");
        return;
      }
      setStep((current) => current + 1);
      return;
    }
    if (emptyMode) {
      const businessName = identityMode === "freelancer"
        ? draft.businessName.trim() || draft.ownerName.trim()
        : draft.businessName.trim();
      if (identityMode !== "later" && !businessName) {
        setMessage(identityMode === "freelancer" ? "Add the name that should appear on invoices." : "Add the business name that should appear on invoices.");
        return;
      }
      if (identityMode !== "later" && !draft.countryCode) {
        setMessage("Choose the country for this invoice identity.");
        return;
      }
      await runComplete({
        ...draft,
        businessName: identityMode === "later" ? "" : businessName,
        legalName: identityMode === "company" ? draft.legalName.trim() : businessName,
        businessType: identityMode,
        workspaceName: workspaceNameFor({ ...draft, businessName }, identityMode),
      });
      return;
    }
    if (!hasFullName(draft.ownerName)) {
      setMessage("Enter your first and last name so assignments and activity are clear.");
      return;
    }
    await runComplete(draft);
  };

  const skipSetup = async () => {
    if (!skip) return;
    setBusy(true);
    setMessage("");
    try {
      const result = await skip();
      if (!result?.ok) setMessage(result?.error || "This workspace could not be opened in your browser.");
    } catch {
      setMessage("This workspace could not be opened in your browser.");
    } finally {
      setBusy(false);
    }
  };

  const businessLogoPicker = <div className="is2-local-business-logo">
    {draft.businessLogo ? <img src={draft.businessLogo} alt="Business logo preview" /> : <span><OfficeIcon size={18} /></span>}
    <div><strong>Business image</strong><small>Optional logo shown in the sidebar and on invoices · PNG, JPG, or SVG up to 2 MB</small><label className="is2-button is2-button--quiet">Choose image<input data-cy="local-business-logo" type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={changeBusinessLogo} /></label></div>
  </div>;
  const cityOptions = CITY_OPTIONS_BY_COUNTRY[draft.countryCode] || [];
  const cityField = cityOptions.length ? <label>City or locality, optional<CustomSelect
    value={customCity ? "__other__" : draft.location}
    onChange={(event) => {
      if (event.target.value === "__other__") {
        setCustomCity(true);
        setDraft((current) => ({ ...current, location: "" }));
      } else {
        setCustomCity(false);
        setDraft((current) => ({ ...current, location: event.target.value }));
      }
    }}
  ><option value="">Choose city</option>{cityOptions.map((city) => <option key={city} value={city}>{city}</option>)}<option value="__other__">Another city</option></CustomSelect>{customCity ? <input autoFocus value={draft.location} onChange={update("location")} placeholder="Type city or locality" /> : null}</label> : <label>City or locality, optional<input value={draft.location} onChange={update("location")} placeholder={draft.countryCode ? "Type city or locality" : "Choose a country first"} disabled={!draft.countryCode} /></label>;

  if (emptyMode) {
    const [stepLabel, stepTitle, stepDescription] = STEP_COPY[step];
    return <ModalLayer close={close} className="is2-local-setup-layer">
      <section className="is2-local-onboarding is-stepped" role="dialog" aria-modal="true" aria-labelledby="local-onboarding-title" data-cy="local-onboarding" data-step={step + 1}>
        <header className="is2-local-onboarding__topbar"><Brand compact /><IconButton label="Back to workspace choices" onClick={close}><CrossIcon size={17} /></IconButton></header>
        <div className="is2-local-onboarding__shell">
          <aside>
            <p className="is2-eyebrow">Local setup</p>
            <h2 id="local-onboarding-title">A workspace that starts with you.</h2>
            <p>Three small choices, then the workspace is ready.</p>
            <ol>{STEP_COPY.map(([label], index) => <li key={label} className={index === step ? "is-active" : index < step ? "is-complete" : ""} aria-current={index === step ? "step" : undefined}><span>{index + 1}</span><strong>{label}</strong></li>)}</ol>
            <div className="is2-local-onboarding__privacy"><ShieldIcon size={17} /><span><strong>Private to this browser</strong><small>No account or server is connected. Export a backup before clearing browser data.</small></span></div>
          </aside>
          <form onSubmit={submit}>
            <section className="is2-local-onboarding__step">
              <header><p className="is2-eyebrow">Step {step + 1} of 3 · {stepLabel}</p><h3>{stepTitle}</h3><span>{stepDescription}</span></header>
              {step === 0 ? <div className="is2-local-onboarding__fields">
                <label>Full name<input data-cy="local-owner-name" autoFocus value={draft.ownerName} onChange={update("ownerName")} placeholder="e.g. Alex Morgan" autoComplete="name" required /></label>
                <label>Email, optional<input data-cy="local-owner-email" type="email" value={draft.email} onChange={update("email")} placeholder="you@example.com" /></label>
              </div> : null}
              {step === 1 ? <div className="is2-local-focus-options">{FOCUS_OPTIONS.map(([id, label, detail]) => <button type="button" key={id} className={draft.focus === id ? "is-active" : ""} aria-pressed={draft.focus === id} onClick={() => chooseFocus(id)}><span><strong>{label}</strong><small>{detail}</small></span><i /></button>)}</div> : null}
              {step === 2 ? <>
                <div className="is2-local-identity-options">{IDENTITY_OPTIONS.map(([id, label, detail]) => <button type="button" key={id} data-cy={`local-identity-${id}`} className={identityMode === id ? "is-active" : ""} aria-pressed={identityMode === id} onClick={() => chooseIdentity(id)}><span><strong>{label}</strong><small>{detail}</small></span><i /></button>)}</div>
                {identityMode !== "later" ? <div className="is2-local-onboarding__fields is-business-fields">
                  {businessLogoPicker}
                  <label>{identityMode === "freelancer" ? "Name on invoices" : "Business name"}<input data-cy="local-business-name" value={draft.businessName} onChange={update("businessName")} placeholder={identityMode === "freelancer" ? "Your professional name" : "Your business"} required /></label>
                  {identityMode === "company" ? <label>Legal name, optional<input value={draft.legalName} onChange={update("legalName")} placeholder="Registered legal name" /></label> : null}
                  <label>Country<CustomSelect data-cy="local-business-country" value={draft.countryCode} onChange={updateCountry} required><option value="">Choose country</option>{COUNTRY_OPTIONS.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}</CustomSelect></label>
                  {cityField}
                  <label>Default currency<CustomSelect value={draft.currency} onChange={update("currency")}><option>EUR</option><option>USD</option><option>GBP</option><option>CHF</option></CustomSelect></label>
                </div> : <div className="is2-local-onboarding__blank-state"><SavedIcon size={18} /><span><strong>Nothing will be pre-filled.</strong><small>You can add an issuing business from Settings whenever you are ready to create an invoice.</small></span></div>}
              </> : null}
              {message ? <p className="is2-login__message" role="status">{message}</p> : null}
            </section>
            <footer>
              <button type="button" className="is2-text-button" disabled={busy} onClick={skipSetup}>Skip setup</button>
              <div>{step > 0 ? <button type="button" className="is2-button is2-button--quiet" disabled={busy} data-cy="local-onboarding-back" onClick={() => { setMessage(""); setStep((current) => current - 1); }}>Back</button> : null}<button type="submit" className="is2-button is2-button--primary" data-cy={step < 2 ? "local-onboarding-next" : "local-onboarding-finish"} disabled={busy || (step === 0 && !hasFullName(draft.ownerName))}>{busy ? "Creating workspace..." : step < 2 ? "Continue" : "Open workspace"}</button></div>
            </footer>
          </form>
        </div>
      </section>
    </ModalLayer>;
  }

  return <ModalLayer close={close}>
    <section className="is2-local-onboarding" role="dialog" aria-modal="true" aria-labelledby="local-onboarding-title" data-cy="local-onboarding">
      <header>
        <div><p className="is2-eyebrow">Local setup</p><h2 id="local-onboarding-title">Make Invoice Studio yours.</h2><span>Set up the business that will issue invoices. You can add clients, services, and tax defaults afterward.</span></div>
        <IconButton label="Back to workspace choices" onClick={close}><CrossIcon size={17} /></IconButton>
      </header>
      <div className="is2-local-onboarding__notice"><ShieldIcon size={18} /><span><strong>Private to this browser</strong><small>No account or server is connected. Your records remain on this device until you export a backup or clear browser data.</small></span></div>
      <form onSubmit={submit}>
        <section>
          <div className="is2-local-onboarding__section-heading"><OfficeIcon size={17} /><span><strong>Issuing business</strong><small>The legal identity shown on invoices.</small></span></div>
          {businessLogoPicker}
          <div className="is2-settings-form is2-settings-form--two">
            <label>Business name<input required value={draft.businessName} onChange={update("businessName")} placeholder="Your business" /></label>
            <label>Legal name<input value={draft.legalName} onChange={update("legalName")} placeholder="Optional registered name" /></label>
            <label>Country<CustomSelect required value={draft.countryCode} onChange={updateCountry}><option value="">Choose country</option>{COUNTRY_OPTIONS.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}</CustomSelect></label>
            {cityField}
            <label>Default currency<CustomSelect value={draft.currency} onChange={update("currency")}><option>EUR</option><option>USD</option><option>GBP</option><option>CHF</option></CustomSelect></label>
          </div>
        </section>
        <section>
          <div className="is2-local-onboarding__section-heading"><SavedIcon size={17} /><span><strong>Your profile</strong><small>Used in the local activity history.</small></span></div>
          <div className="is2-settings-form is2-settings-form--two">
            <label>Full name<input required value={draft.ownerName} onChange={update("ownerName")} placeholder="First and last name" autoComplete="name" /></label>
            <label>Billing email<input type="email" value={draft.email} onChange={update("email")} placeholder="Optional" /></label>
          </div>
        </section>
        {message ? <p className="is2-login__message" role="status">{message}</p> : null}
        <footer><button type="button" className="is2-button is2-button--quiet" onClick={close}>Back</button><button type="submit" className="is2-button is2-button--primary" disabled={busy || !draft.businessName.trim() || !hasFullName(draft.ownerName) || !draft.countryCode}>{busy ? "Creating workspace..." : "Create local workspace"}</button></footer>
      </form>
    </section>
  </ModalLayer>;
};

export default LocalWorkspaceOnboarding;
